import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FastProtectedRoute from "./components/FastProtectedRoute";

// Eager load critical components (above the fold)
import Home from "./pages/Home";

// Lazy load all other components (removed cache busting as it was breaking imports)
const Aboutus = lazy(() => import("./pages/Aboutus"));
const Services = lazy(() => import("./pages/Services"));
const Destinations = lazy(() => import("./pages/Destinations"));
const Associates = lazy(() => import("./pages/AllAssociates"));
const AssociatesDynamic = lazy(() => import("./pages/AllAssociatesDynamic"));
const InstitutionDetail = lazy(() => import("./pages/InstitutionDetail"));
const TestDynamic = lazy(() => import("./pages/TestDynamic"));
const Contact = lazy(() => import("./pages/Contact"));
const CreateAccount = lazy(() => import("./pages/CreateAccount"));
const Us = lazy(() => import("./pages/Us"));
const Canada = lazy(() => import("./pages/Canada-Layout3"));
const Spain = lazy(() => import("./pages/Spain"));
const Uk = lazy(() => import("./pages/Uk"));
const NewZealand = lazy(() => import("./pages/NewZealand"));
const Australia = lazy(() => import("./pages/Australia"));
const Germany = lazy(() => import("./pages/Germany"));
const France = lazy(() => import("./pages/France"));
const Italy = lazy(() => import("./pages/Italy"));
const Dubai = lazy(() => import("./pages/Dubai"));
const Finland = lazy(() => import("./pages/Finland"));
const Singapore = lazy(() => import("./pages/Singapore"));
const Poland = lazy(() => import("./pages/Poland"));
const Cyprus = lazy(() => import("./pages/Cyprus"));
const Malta = lazy(() => import("./pages/Malta"));
const Ireland = lazy(() => import("./pages/Ireland"));
const StudentLogin = lazy(() => import("./pages/StudentLogin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AgentLogin = lazy(() => import("./pages/AgentLogin"));
const CourseFinder = lazy(() => import("./components/CourseFinder"));

// Dashboard components - these will get fresh versions due to the cleared cache
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const AgentDashboard = lazy(() => import("./components/AgentDashboard"));
const AgentStudents = lazy(() => import("./components/AgentStudents"));
const StudentProfileView = lazy(() => import("./pages/StudentProfileView"));
const Commission = lazy(() => import("./pages/Commission"));
const StudentDsh = lazy(() => import("./components/StudentDsh"));

const Login = lazy(() => import("./pages/Login"));
const Student = lazy(() => import("./components/Student"));
const Recruitment = lazy(() => import("./components/Recruitment"));
const Blogs = lazy(() => import("./pages/Blogs"));
const Institutions = lazy(() => import("./components/Institutions"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const Fees = lazy(() => import("./pages/Fees"));
const EnrollStudent = lazy(() => import("./pages/EnrollStudents"));
const EnrollStdAgent = lazy(() => import("./pages/EnrollStdAgent"));
const AgentGuide = lazy(() => import("./pages/AgentGuide"));
const StudentGuide = lazy(() => import("./pages/StudentGuide"));
const TermsCondition = lazy(() => import("./pages/TermsCondition"));
const PrivacyCookies = lazy(() => import("./pages/PrivacyCookies"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Support = lazy(() => import("./pages/Support"));
const CoursePerformanceTest = lazy(
  () => import("./pages/CoursePerformanceTest"),
);
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));
const StudentsHistory = lazy(() => import("./pages/StudentsHistory"));
const AgentsHistory = lazy(() => import("./pages/AgentsHistory"));
const SubAdminUS = lazy(() => import("./pages/SubAdminUS"));
const SubAdminAusNZ = lazy(() => import("./pages/SubAdminAusNZ"));
const SubAdminUK = lazy(() => import("./pages/SubAdminUK"));
const SubAdminCanada = lazy(() => import("./pages/SubAdminCanada"));
const SubAdminEurope = lazy(() => import("./pages/SubAdminEurope"));
const CourseRequest = lazy(() => import("./pages/CourseRequest"));
const ChatTest = lazy(() => import("./components/ChatTest"));
const PaymentTest = lazy(() => import("./components/PaymentTest"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const FinalAgentRegistration = lazy(
  () => import("./pages/FinalAgentRegistration"),
);
const InitialAgentRegistration = lazy(
  () => import("./pages/InitialAgentRegistration"),
);
const DocumentSign = lazy(() => import("./components/DocumentSign"));
const DocumentSignDemo = lazy(() => import("./pages/DocumentSignDemo"));
const AgentSignup = lazy(() => import("./components/AgentSignup"));

// Optimized loading component with skeleton
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "50vh",
      fontSize: "18px",
    }}
  >
    <div
      style={{
        animation: "pulse 1.5s ease-in-out infinite alternate",
      }}
    >
      Loading...
    </div>
    <style>{`
      @keyframes pulse {
        0% { opacity: 0.6; }
        100% { opacity: 1; }
      }
    `}</style>
  </div>
);

const App = () => {
  return (
    <>
      <Router>
        {/* <AIChatbot /> */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          style={{ zIndex: 9999 }}
        />
        {/* <AdminDashboard /> */}
        {/* <TopToBottom /> */}
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Main Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/terms-conditions" element={<TermsCondition />} />
            <Route
              path="/privacy-cookies-policy"
              element={<PrivacyCookies />}
            />
            <Route path="/support" element={<Support />} />
            <Route
              path="/course-performance-test"
              element={<CoursePerformanceTest />}
            />
            <Route path="/guide" element={<AgentGuide />} />
            <Route path="/student-guide" element={<StudentGuide />} />
            <Route path="/about" element={<Aboutus />} />
            <Route path="/services" element={<Services />} />
            <Route path="/destinations" element={<Destinations />} />
            <Route path="/associates-dynamic" element={<AssociatesDynamic />} />
            <Route
              path="/associates-dynamic/:slug"
              element={<InstitutionDetail />}
            />
            <Route path="/test-dynamic" element={<TestDynamic />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/plan-studies" element={<Student />} />
            <Route path="/resources" element={<Recruitment />} />
            <Route path="/track-progress" element={<Institutions />} />
            <Route path="/blog" element={<Blogs />} />
            {/* Authentication Routes */}
            <Route path="/register" element={<CreateAccount />} />
            <Route path="/create" element={<CreateAccount />} />
            <Route path="/login" element={<Login />} />
            <Route path="/student-login" element={<StudentLogin />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/agent-login" element={<AgentLogin />} />
            <Route
              path="/agentinitial"
              element={<InitialAgentRegistration />}
            />
            <Route
              path="/agentinitial/"
              element={<InitialAgentRegistration />}
            />
            <Route
              path="/agent/initial"
              element={<InitialAgentRegistration />}
            />
            <Route
              path="/agent/initial/"
              element={<InitialAgentRegistration />}
            />
            <Route path="/agent" element={<FinalAgentRegistration />} />
            <Route path="/agent/" element={<FinalAgentRegistration />} />
            <Route
              path="/initial-agent-registration"
              element={<InitialAgentRegistration />}
            />
            <Route
              path="/finalagentregistration"
              element={<FinalAgentRegistration />}
            />
            <Route path="/stdlogout" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            {/* Country Routes */}
            <Route path="/us" element={<Us />} />
            <Route path="/canada" element={<Canada />} />
            <Route path="/spain" element={<Spain />} />
            <Route path="/uk" element={<Uk />} />
            <Route path="/nz" element={<NewZealand />} />
            <Route path="/aus" element={<Australia />} />
            <Route path="/germany" element={<Germany />} />
            <Route path="/france" element={<France />} />
            <Route path="/italy" element={<Italy />} />
            <Route path="/dubai" element={<Dubai />} />
            <Route path="/finland" element={<Finland />} />
            <Route path="/singapore" element={<Singapore />} />
            <Route path="/poland" element={<Poland />} />
            <Route path="/cyprus" element={<Cyprus />} />
            <Route path="/malta" element={<Malta />} />
            <Route path="/ireland" element={<Ireland />} />
            {/* Protected Dashboard Routes */}
            <Route
              path="/student-dash"
              element={
                <FastProtectedRoute requiredRole="student">
                  <StudentDsh />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/course-finder"
              element={
                <FastProtectedRoute allowedRoles={["student", "agent"]}>
                  <CourseFinder />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/admin-dashboard"
              element={
                <FastProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/agent-dashboard"
              element={
                <FastProtectedRoute requiredRole="agent">
                  <AgentDashboard />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/students"
              element={
                <FastProtectedRoute requiredRole="agent">
                  <AgentStudents />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/student/:studentId"
              element={
                <FastProtectedRoute requiredRole="agent">
                  <StudentProfileView />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/commission"
              element={
                <FastProtectedRoute requiredRole="agent">
                  <Commission />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/sub-admin-usa-dashboard"
              element={
                <FastProtectedRoute requiredRole="sub-admin">
                  <SubAdminUS />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/sub-admin-aus-nz-dashboard"
              element={
                <FastProtectedRoute requiredRole="sub-admin">
                  <SubAdminAusNZ />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/sub-admin-uk-dashboard"
              element={
                <FastProtectedRoute requiredRole="sub-admin">
                  <SubAdminUK />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/sub-admin-canada-dashboard"
              element={
                <FastProtectedRoute requiredRole="sub-admin">
                  <SubAdminCanada />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/sub-admin-europe-dashboard"
              element={
                <FastProtectedRoute requiredRole="sub-admin">
                  <SubAdminEurope />
                </FastProtectedRoute>
              }
            />
            {/* Other Protected Routes */}
            <Route
              path="/fees"
              element={
                <FastProtectedRoute allowedRoles={["student", "agent"]}>
                  <Fees />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/docs"
              element={
                <FastProtectedRoute allowedRoles={["student", "agent"]}>
                  <EnrollStdAgent />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/enroll"
              element={
                <FastProtectedRoute allowedRoles={["student", "agent"]}>
                  <EnrollStudent />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/request"
              element={
                <FastProtectedRoute allowedRoles={["student", "agent"]}>
                  <CourseRequest />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/payment-history"
              element={
                <FastProtectedRoute
                  allowedRoles={["admin", "agent", "sub-admin"]}
                >
                  <PaymentHistory />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/students-history"
              element={
                <FastProtectedRoute
                  allowedRoles={["admin", "agent", "sub-admin"]}
                >
                  <StudentsHistory />
                </FastProtectedRoute>
              }
            />
            <Route
              path="/agents-history"
              element={
                <FastProtectedRoute allowedRoles={["admin", "sub-admin"]}>
                  <AgentsHistory />
                </FastProtectedRoute>
              }
            />
            {/* Payment Success Route */}
            <Route path="/payment-success" element={<PaymentSuccess />} />
            {/* Document Signing Routes */}
            <Route
              path="/sign/document/mail/:mailId/:docId"
              element={<DocumentSign />}
            />
            <Route
              path="/document/sign/:mailId/:docId"
              element={<DocumentSign />}
            />
            <Route path="/document-sign-demo" element={<DocumentSignDemo />} />
            {/* Agent Signup Route */}
            <Route path="/web/signup" element={<AgentSignup />} />
            {/* Debug Routes */}
            <Route path="/chat-test" element={<ChatTest />} />
            <Route path="/payment-test" element={<PaymentTest />} />
          </Routes>
        </Suspense>
      </Router>
    </>
  );
};

export default App;
